/* eslint-disable */
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { Avatar } from "./ui.pb";

/** An action to perform when the customer taps a button. */
export interface BlockerAction {
  /**
   * The title displayed on the button that the customer will use to initiate this action.
   * The iOS client requires that this field is non-empty.
   */
  text?: string;
  localized_text?: LocalizableString;
  /** Confirmation dialog is displayed before the action is executed. */
  confirmation_dialog?:
    | BlockerActionConfirmationDialog
    | undefined;
  /** Confirmation half sheet displayed before the action is executed. */
  confirmation_sheet?: BlockerActionConfirmationSheet | undefined;
  end_flow_action?: BlockerActionEndFlowAction | undefined;
  menu_action?: BlockerActionMenuAction | undefined;
  open_url_action?: BlockerActionOpenURLAction | undefined;
  skip_blocker_action?: BlockerActionSkipBlockerAction | undefined;
  submit_action?: BlockerActionSubmitAction | undefined;
  internal_navigation_action?: BlockerActionInternalNavigationAction | undefined;
  sign_out_action?: BlockerActionSignOutAction | undefined;
  share_action?: BlockerActionShareFileAction | undefined;
  view_action?: BlockerActionViewFileAction | undefined;
  dialog_action?: BlockerActionDialogAction | undefined;
  copy_action?: BlockerActionCopyAction | undefined;
  back_navigation_action?: BlockerActionBackNavigationAction | undefined;
  share_text_action?: BlockerActionShareTextAction | undefined;
}

/**
 * Describes an alert dialog that can be displayed before performing this blocker action.
 *
 * Formatting guidelines can be found here:
 * https://developer.apple.com/design/human-interface-guidelines/ios/views/alerts/
 */
export interface BlockerActionConfirmationDialog {
  /**
   * An optional short title summarizing the action being confirmed, such as “Confirm Deletion”.
   * This should be in title case.
   */
  title?: string;
  /**
   * A required message with details about the action being confirmed, such as
   * “This will permanently delete Oscar’s phone number.”
   *
   * This should use complete sentences and be in sentence (normal) case.
   */
  message?: string;
  /**
   * The text of button used to confirm the action. This should be as descriptive of the
   * action as possible, such as “Delete”. If nothing is sufficiently short then use “OK”.
   * This field is required.
   */
  confirm_button_text?: string;
  /**
   * The text of button used to cancel performing the action. This will usually be something
   * like “Cancel” or “Later”.
   * This field is required.
   */
  cancel_button_text?: string;
}

/**
 * Bottom sheet shown before blockerAction fires giving the user a message plus the option to "go back"
 * instead of executing the blockerAction. Example design:
 *  ______________________________________________________________
 * /                                                              \
 * |                              (!)                             |
 * |                                                              |
 * |         Amount is too low to complete $216.36 order          |
 * |                     Good Pour Coffee                         |
 * |                                                              |
 * | You'll need at least $25 to use Cash App Pay for this order. |
 * |                                                              |
 * |                 (Continue)    (Go Back)                      |
 * ----------------------------------------------------------------
 */
export interface BlockerActionConfirmationSheet {
  /**
   * Avatar/icon displayed on the top, e.g. (!)
   *
   * Not optional.
   */
  avatar?: Avatar;
  /**
   * e.g. "Amount is too low to complete $216.36 order". Displayed below avatar.
   *
   * Not optional.
   */
  title?: string;
  /**
   * e.g. Text below title, e.g. "Good Pour Coffee".
   *
   * Optional.
   */
  subtitle?: string;
  /**
   * e.g. Main body text, e.g. "You'll need at least $25 to use Cash App Pay for this order."
   *
   * Not optional.
   */
  message?: string;
  /**
   * e.g. "Continue". Allows user to top up with the amount they selected. Tapping on the button
   * should result in the blockerAction being executed. This will be applied to the "primary"
   * green button by default.
   *
   * Not optional.
   */
  confirm_button_text?: string;
  /**
   * e.g. "Go back". Takes user back to the previous blocker/screen when pressed. This will be
   * applied to the "secondary" grey button by default.
   *
   * Not optional.
   */
  return_button_text?: string;
  /**
   * When set to true, the primary and secondary buttons switch
   * positions but keep the positional styling, e.g.:
   *
   *                                          grey/left button          green/right button
   * ------------------------------------------------------------------------------------------
   * invert_button_treatment = false ==>      (Go Back)                 (Continue)
   * Action taken when pressed       ==>      navigates back            executes blockerAction
   * ------------------------------------------------------------------------------------------
   * invert_button_treatment = true  ==>      (Continue)                (Go Back)
   * Action taken when pressed       ==>      executes blockerAction    navigates back
   *
   * Optional, defaults to false.
   */
  invert_button_treatment?: boolean;
  /** The action to perform after scheduled_action_delay_ms. */
  scheduled_action?: BlockerAction;
  /** The amount of time to wait before performing scheduled_action. */
  scheduled_action_delay_ms?: number;
  /** Indicates which confirmation sheet variant should be shown */
  variant?: BlockerActionConfirmationSheetVariant;
}

/** Set of variants used to indicate which confirmation sheet variant should be shown */
export enum BlockerActionConfirmationSheetVariant {
  CONTROL = "CONTROL",
  VARIANT_A = "VARIANT_A",
}

/** Ends and dismisses the current flow. */
export interface BlockerActionEndFlowAction {
  result?: BlockerActionEndFlowActionResult;
  /**
   * If not set, no interaction is made with the server.
   * If set, a call will be made to the `end-flow` endpoint in the background
   * after dismissing the flow with this as the `EndFlowRequest#action_id`
   */
  notify_end_flow_id?: string;
}

export enum BlockerActionEndFlowActionResult {
  COMPLETE = "COMPLETE",
  CANCELLED = "CANCELLED",
}

/** Displays a sheet to the customer with a list of additional options. */
export interface BlockerActionMenuAction {
  actions?: BlockerAction[];
}

/**
 * Opens the given URL.
 *
 * The URL should be opened in a web browser.
 * If end_flow is false or not provided, the flow will be left intact (not dismissed).
 */
export interface BlockerActionOpenURLAction {
  url?: string;
  end_flow?: boolean;
  /** Defines how the URL should be opened. */
  behavior?: BlockerActionOpenURLActionBehavior;
}

export enum BlockerActionOpenURLActionBehavior {
  /**
   * DEFAULT - On Android, this Behavior causes the URL to open in a Custom Tab
   * (see: https://developer.chrome.com/docs/android/custom-tabs/overview/).
   * On iOS, the URL will be opened via an external browser.
   */
  DEFAULT = "DEFAULT",
  /** REDIRECT - On both iOS and Android, this Behavior causes the URL to open via an external browser. */
  REDIRECT = "REDIRECT",
}

/** Moves to the next blocker without server interaction. */
export interface BlockerActionSkipBlockerAction {
}

/**
 * Calls the `submit-form` end point (which will update the scenario plan), then advances to
 * the next blocker.
 *
 * The `id` value should be included in the `SubmitFormRequest` when calling `submit-form`.
 */
export interface BlockerActionSubmitAction {
  id?: string;
  /**
   * If set, the client will display this label along side the loading spinner while it's waiting
   * for the server's response.
   */
  loading_label?: string;
  /**
   * The direction that the client should animate towards when displaying the loading spinner and
   * resulting server-provided blocker. Added in version 3.43. Defaults to FORWARD.
   */
  direction?: BlockerActionSubmitActionAnimationDirection;
}

export enum BlockerActionSubmitActionAnimationDirection {
  FORWARD = "FORWARD",
  BACKWARD = "BACKWARD",
}

/** Navigates to a different part of the app. */
export interface BlockerActionInternalNavigationAction {
  support?: BlockerActionInternalNavigationActionSupport | undefined;
  client_route?: BlockerActionInternalNavigationActionClientRoute | undefined;
}

/** Abandons any flows in progress and starts a support flow at the given support_flow_node. */
export interface BlockerActionInternalNavigationActionSupport {
  support_flow_node?: string;
}

/** Performs a client route. */
export interface BlockerActionInternalNavigationActionClientRoute {
  url?: string;
}

/** Abandons any flows in progress and signs the user out. */
export interface BlockerActionSignOutAction {
}

/** Opens a client native share sheet. */
export interface BlockerActionShareFileAction {
  file_url?: string;
  title?: string;
  extension?: string;
  loading_text?: string;
  options?: BlockerActionShareFileActionShareOption[];
}

export interface BlockerActionShareFileActionEmailOption {
  subject?: string;
  body?: string;
}

export interface BlockerActionShareFileActionViewOption {
}

export interface BlockerActionShareFileActionShareOption {
  email?: BlockerActionShareFileActionEmailOption | undefined;
  view?: BlockerActionShareFileActionViewOption | undefined;
}

/**
 * Opens a client native share sheet for sharing text data.
 * Please note the email_subject is skipped on Samsung devices with app version >= 3.78.
 * See CASHA-10660 for more details.
 */
export interface BlockerActionShareTextAction {
  /** Required. The actual text data to share (ie. via SMS, email, etc) */
  text_data?: string;
  /** This optional title is used for a preview when sharing */
  title?: string;
  /**
   * This optional email subject is used if the customer selects email sharing
   * in the share sheet, except on Samsung devices with app version >= 3.78.
   */
  email_subject?: string;
  /** when populated, the app will open the sms app with message populated with text_data and recipient populated with sms_recipient. */
  sms_recipient?: string;
  /** Only used for iOS devices.The action triggered after getting back from the sms app to the app (ios only) */
  after_successful_sms_action?: BlockerActionSubmitAction;
}

/** Opens a file with a client viewer. */
export interface BlockerActionViewFileAction {
  file_url?: string;
  title?: string;
  extension?: string;
  loading_text?: string;
}

/**
 * Displays a dialog.
 * This can eventually replace the existing confirmation dialog.
 */
export interface BlockerActionDialogAction {
  /** Required. */
  title?: string;
  /** Required. */
  message?: string;
  /** Required. */
  primary_button?: BlockerActionDialogActionButton;
  /** Optional. */
  secondary_button?: BlockerActionDialogActionButton;
}

export interface BlockerActionDialogActionButton {
  /** Required. */
  text?: string;
  /**
   * Optional. If set, this action will be called when the `primary_button` is tapped.
   * If null then this button simply dismisses the dialog.
   */
  action?: BlockerAction;
  style?: BlockerActionDialogActionButtonStyle;
}

export enum BlockerActionDialogActionButtonStyle {
  DEFAULT = "DEFAULT",
  CANCEL = "CANCEL",
  DESTRUCTIVE = "DESTRUCTIVE",
}

/** A blocker action that will allow the user to copy text to their clipboard. */
export interface BlockerActionCopyAction {
  /** The (non-optional) text to be copied to the clipboard when the action is performed. */
  text?: string;
}

/**
 * [Experimental] A blocker action that causes the mobile client to minimize Cash App and navigate
 * back to the previous application on their mobile OS app stack.
 */
export interface BlockerActionBackNavigationAction {
  /** A fallback URL clients can use for forward navigation if it needs to do so for any reason. */
  redirect_url?: string;
}
